/**
 * src/state/useTheme.js
 * Per-user GUI theme switcher.
 * Preference is stored on the user object in the database.
 * localStorage is used as a cache so the correct theme shows before login.
 */
import { useState, useEffect, useCallback } from 'react';
import { AURORA_DARK_VARS, AURORA_LIGHT_VARS } from '../layouts/aurora/palettes.js';

// ── Theme definitions ────────────────────────────────────────────────────────
//
// All three themes follow the strict 70 / 20 / 10 colour rule:
//   70%  neutral surfaces   (--bg, --bg2, --bg3, --bg4 + insets)
//   20%  contrast structure (--fg, --fg2, --fg3 + hairline borders)
//   10%  accent             (--accent + subtle washes)
//
// The accent appears in only four places per screen:
//   1. Active navigation item    2. Primary KPI / headline metric
//   3. Primary call-to-action    4. Keyboard focus ring
// Decorative uses of the accent have been removed — see globals.css.
//
// Semantic colours (--warn, --danger) are not part of the 10% accent budget;
// they may appear wherever they carry meaning.
//
// ── Severity ramp ────────────────────────────────────────────────────────────
// The product rates a lot of things on a graded severity/priority scale
// (findings, pen-test results, DR test outcomes, action priorities). That
// needs more steps than --warn / --danger can carry, so the ramp is its own
// set of tokens — a heat scale that stays legible on every theme:
//
//   --critical  worst      red
//   --high                 rose
//   --medium               orange
//   --low                  yellow
//   --neutral   none/N-A   muted grey — also "expired", "unknown", "informational"
//
// --neutral is each theme's own --fg3, so a neutral chip always harmonises
// with that theme's tertiary text instead of importing a cool slate grey
// into a warm palette.
//
// The ramp is NOT a replacement for --danger / --warn / --ok / --info. Those
// stay what they are: a generic bad-or-destructive state (--danger, used by
// .btn-danger), a generic caution (--warn), a generic good state (--ok) and a
// generic informational state (--info). Reach for the ramp only when
// something is being GRADED.
//
// Purely CATEGORICAL hues — colours that distinguish a type or stage but
// carry no good/bad meaning (workflow stage, assessment type, swimlane) —
// are theme-invariant and live in src/styles/globals.css as --cat-* next to
// the --ev-* evidence tokens, not here.
//
const THEMES = {
  midnight: {
    id: 'midnight',
    label: 'Midnight',
    description: 'Dark theme — warm-neutral darks, off-white contrast, turquoise accent.',
    preview: { accent: '#5eead4', bg: '#13110f', fg: '#ede7dc' },
    vars: {
      // 70% — surfaces
      '--bg': '#13110f',
      '--bg2': '#1b1916',
      '--bg3': '#24211c',
      '--bg4': '#2d2a24',
      // 20% — contrast (text + hairlines)
      '--fg': '#ede7dc',
      '--fg2': '#a89f94',
      '--fg3': '#6b6460',
      '--fg4': '#55504b', // quaternary — more muted than fg3 (disabled/placeholder text)
      '--border': 'rgba(237,231,220,0.07)',
      '--border2': 'rgba(237,231,220,0.14)',
      // 10% — accent (turquoise-green-blue)
      '--accent': '#5eead4',
      '--accent2': '#2dd4bf',
      '--accent-ink': '#07181a', // Readable text on top of accent fill
      '--accent-bg': 'rgba(94,234,212,0.10)',
      '--accent-border': 'rgba(94,234,212,0.18)',
      '--accent-border2': 'rgba(94,234,212,0.12)',
      '--accent-border3': 'rgba(94,234,212,0.20)',
      '--accent-border4': 'rgba(94,234,212,0.22)',
      '--accent-subtle': 'rgba(94,234,212,0.05)',
      // Semantic
      '--ok': '#4ade80',
      '--ok-bg': 'rgba(74,222,128,0.10)',
      '--warn': '#f4b266',
      '--warn-bg': 'rgba(244,178,102,0.10)',
      '--danger': '#f08a8f',
      '--danger-bg': 'rgba(240,138,143,0.10)',
      '--info': '#60a5fa',
      '--info-bg': 'rgba(96,165,250,0.10)',
      // Severity ramp — see the note above THEMES
      '--critical': '#ef4444',
      '--critical-bg': 'rgba(239,68,68,0.10)',
      '--high': '#fb7185',
      '--high-bg': 'rgba(251,113,133,0.10)',
      '--medium': '#fb923c',
      '--medium-bg': 'rgba(251,146,60,0.10)',
      '--medium-strong': '#ea580c', // deeper stop for a --medium gradient
      '--low': '#facc15',
      '--low-bg': 'rgba(250,204,21,0.10)',
      '--neutral': '#6b6460', // = --fg3
      '--neutral-bg': 'rgba(107,100,96,0.14)',
      // Insets
      '--bg-inset': 'rgba(19,17,15,0.6)',
      '--bg-inset2': 'rgba(19,17,15,0.7)',
    },
  },
  sandstone: {
    id: 'sandstone',
    label: 'Sandstone',
    description: 'Light theme — near-white warm surfaces, near-black contrast, deep-teal accent.',
    preview: { accent: '#0e9488', bg: '#fafaf7', fg: '#15130f' },
    vars: {
      // 70% — surfaces (near-white warm)
      '--bg': '#fafaf7',
      '--bg2': '#ffffff',
      '--bg3': '#f2efea',
      '--bg4': '#e8e4dc',
      // 20% — contrast (near-black text + hairlines)
      '--fg': '#15130f',
      '--fg2': '#5c544c',
      '--fg3': '#8a8278',
      '--fg4': '#aba49b', // quaternary — more muted than fg3 (disabled/placeholder text)
      '--border': 'rgba(21,19,15,0.07)',
      '--border2': 'rgba(21,19,15,0.14)',
      // 10% — accent (darker turquoise for AA on white)
      '--accent': '#0e9488',
      '--accent2': '#0b7a70',
      '--accent-ink': '#ffffff', // Readable text on top of accent fill
      '--accent-bg': 'rgba(14,148,136,0.08)',
      '--accent-border': 'rgba(14,148,136,0.20)',
      '--accent-border2': 'rgba(14,148,136,0.14)',
      '--accent-border3': 'rgba(14,148,136,0.24)',
      '--accent-border4': 'rgba(14,148,136,0.28)',
      '--accent-subtle': 'rgba(14,148,136,0.05)',
      // Semantic
      '--ok': '#15803d',
      '--ok-bg': 'rgba(21,128,61,0.08)',
      '--warn': '#b45309',
      '--warn-bg': 'rgba(180,83,9,0.08)',
      '--danger': '#b91c1c',
      '--danger-bg': 'rgba(185,28,28,0.08)',
      '--info': '#2563eb',
      '--info-bg': 'rgba(37,99,235,0.08)',
      // Severity ramp — darker than the dark-theme values so they still meet
      // AA on the near-white surfaces
      '--critical': '#991b1b',
      '--critical-bg': 'rgba(153,27,27,0.08)',
      '--high': '#be123c',
      '--high-bg': 'rgba(190,18,60,0.08)',
      '--medium': '#c2410c',
      '--medium-bg': 'rgba(194,65,12,0.08)',
      '--medium-strong': '#9a3412', // deeper stop for a --medium gradient
      '--low': '#a16207',
      '--low-bg': 'rgba(161,98,7,0.08)',
      '--neutral': '#8a8278', // = --fg3
      '--neutral-bg': 'rgba(138,130,120,0.12)',
      // Insets
      '--bg-inset': 'rgba(242,239,234,0.8)',
      '--bg-inset2': 'rgba(242,239,234,0.9)',
    },
  },
  'aurora-light': {
    id: 'aurora-light',
    label: 'Aurora Light',
    description: 'Aurora theme — cool near-white surfaces, deep-navy text, indigo accent.',
    preview: { accent: '#6366f1', bg: '#f7f8fc', fg: '#0e1124' },
    vars: AURORA_LIGHT_VARS,
  },
  'aurora-dark': {
    id: 'aurora-dark',
    label: 'Aurora Dark',
    description: 'Aurora theme — deep navy surfaces, off-white text, indigo accent.',
    preview: { accent: '#7c83ff', bg: '#0d1124', fg: '#ffffff' },
    vars: AURORA_DARK_VARS,
  },
  ocean: {
    id: 'ocean',
    label: 'Ocean',
    description: 'Brand theme — deep teal-tinted neutrals with bright turquoise accent.',
    preview: { accent: '#5eead4', bg: '#0a1b1d', fg: '#e8f4f2' },
    vars: {
      // 70% — surfaces (deep teal-tinted neutral darks)
      '--bg': '#0a1b1d',
      '--bg2': '#0f2426',
      '--bg3': '#143437',
      '--bg4': '#1b4448',
      // 20% — contrast (off-white teal-tinted)
      '--fg': '#e8f4f2',
      '--fg2': '#7daaa6',
      '--fg3': '#4f7a77',
      '--fg4': '#3d6767', // quaternary — more muted than fg3 (disabled/placeholder text)
      '--border': 'rgba(232,244,242,0.08)',
      '--border2': 'rgba(232,244,242,0.16)',
      // 10% — accent (bright turquoise — the brand colour)
      '--accent': '#5eead4',
      '--accent2': '#2dd4bf',
      '--accent-ink': '#07181a', // Readable text on top of accent fill
      '--accent-bg': 'rgba(94,234,212,0.10)',
      '--accent-border': 'rgba(94,234,212,0.20)',
      '--accent-border2': 'rgba(94,234,212,0.14)',
      '--accent-border3': 'rgba(94,234,212,0.24)',
      '--accent-border4': 'rgba(94,234,212,0.28)',
      '--accent-subtle': 'rgba(94,234,212,0.05)',
      // Semantic
      '--ok': '#4ade80',
      '--ok-bg': 'rgba(74,222,128,0.10)',
      '--warn': '#f4b266',
      '--warn-bg': 'rgba(244,178,102,0.10)',
      '--danger': '#f08a8f',
      '--danger-bg': 'rgba(240,138,143,0.10)',
      '--info': '#60a5fa',
      '--info-bg': 'rgba(96,165,250,0.10)',
      // Severity ramp — see the note above THEMES
      '--critical': '#ef4444',
      '--critical-bg': 'rgba(239,68,68,0.10)',
      '--high': '#fb7185',
      '--high-bg': 'rgba(251,113,133,0.10)',
      '--medium': '#fb923c',
      '--medium-bg': 'rgba(251,146,60,0.10)',
      '--medium-strong': '#ea580c', // deeper stop for a --medium gradient
      '--low': '#facc15',
      '--low-bg': 'rgba(250,204,21,0.10)',
      '--neutral': '#4f7a77', // = --fg3
      '--neutral-bg': 'rgba(79,122,119,0.16)',
      // Insets
      '--bg-inset': 'rgba(10,27,29,0.6)',
      '--bg-inset2': 'rgba(10,27,29,0.7)',
    },
  },
};

const _BUILT_IN_THEME_IDS = Object.keys(THEMES);
const STORAGE_KEY = 'seasuite-theme';

function applyThemeVars(vars) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([prop, val]) => {
    root.style.setProperty(prop, val);
  });
  // Native form chrome (a <select>'s text and popup) follows `color-scheme`,
  // which by default tracks the OS, not the chosen theme — a dark theme on a
  // light OS painted dark-on-dark controls. Derive it from the theme's --bg.
  const m = /^#?([0-9a-f]{6})$/i.exec(String(vars['--bg'] || '').trim());
  if (m) {
    const n = parseInt(m[1], 16);
    const lum = 0.2126 * (n >> 16) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
    root.style.colorScheme = lum < 128 ? 'dark' : 'light';
  }
}

/**
 * Build the merged themes map from built-ins + tenant custom themes.
 * customThemes: array of { id, label, description, preview, vars } pushed
 * from the Theme Designer via state.customThemes.
 */
function buildThemesMap(customThemes) {
  if (!Array.isArray(customThemes) || customThemes.length === 0) return THEMES;
  const merged = { ...THEMES };
  for (const ct of customThemes) {
    if (ct && ct.id && ct.vars) {
      merged[ct.id] = ct;
    }
  }
  return merged;
}

export function useTheme(session, customThemes) {
  const [allThemes, setAllThemes] = useState(() => buildThemesMap(customThemes));

  // Rebuild themes map when customThemes from state change
  useEffect(() => {
    setAllThemes(buildThemesMap(customThemes));
  }, [customThemes]);

  const [themeId, setThemeId] = useState(() => {
    // Prefer the user's DB-stored preference (from session), fall back to localStorage cache
    const fromSession = session?.themeId;
    if (fromSession && (THEMES[fromSession] || buildThemesMap(customThemes)[fromSession]))
      return fromSession;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && (THEMES[stored] || buildThemesMap(customThemes)[stored])
        ? stored
        : 'midnight';
    } catch {
      return 'midnight';
    }
  });

  // When session changes (e.g. login), apply the user's saved theme
  useEffect(() => {
    if (session?.themeId && allThemes[session.themeId]) {
      setThemeId(session.themeId);
    }
  }, [session?.themeId, allThemes]);

  // Apply CSS vars whenever themeId or allThemes changes
  useEffect(() => {
    const theme = allThemes[themeId] || allThemes.midnight || THEMES.midnight;
    applyThemeVars(theme.vars);
  }, [themeId, allThemes]);

  const switchTheme = useCallback(
    (id) => {
      if (!allThemes[id]) return;
      setThemeId(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {}
    },
    [allThemes]
  );

  const themeIds = Object.keys(allThemes);

  return { themeId, switchTheme, themes: allThemes, themeIds };
}
